/* eslint-disable no-nested-ternary */
import React, { useEffect, useRef, useState } from "react";

import { Container, Grid, Pagination, PaginationItem, PaginationRenderItemParams, styled, useMediaQuery } from "@mui/material";
import { Button, Typography } from "design_system/src";
import { ReactComponent as Filter } from "design_system/src/static/icons/filter-descending.svg";

import useAlliance from "hooks/useAlliance";
import MetaTags from "components/head/MetaTags";
import CatalogBanner from "../catalogBanner/CatalogBanner";
import SkeletonCard from "../skeletonCard/SkeletonCard";
import OzonCardVehiclesPagination from "../ozonCardVehiclesPagination/OzoneCardVehiclesPagination";
import useScrollDirection from "../../hooks/useScrollDirection";
import useSkus from "../../hooks/useSkus";
import { getSkus } from "../../models/inventory/controllers/sku.controller";
import { TSku } from "../../models/inventory/interfaces/sku.interface";
import { SkuFilterOptions, toServerFilters } from "../../models/inventory/helpers/skuFilters.helpers";
import SkuSideBar from "./sku/SkuSideBar";
import SkuFiltersModal from "./sku/SkuFiltersModal";
import SortSkus from "./sku/SortSkus";
import SkuSearchInput from "./sku/SkuSearchInput";
import { SkuFilterTagList } from "./sku/SkuFilterTagList";
import { useSkuCatalogFilters } from "./sku/useSkuCatalogFilters";

import "./NewCatalog.css";


const StyledPaginationItem = styled(PaginationItem)(({ theme }) => ({
  borderRadius: "10px",
  minWidth: 36,
  fontSize: "14px",
  backgroundColor: "var(--neutral_100)",
  "&:hover": {
    borderRadius: "10px",
    backgroundColor: "var(--primary_50)",
    color: "var(--primary_300)",
  },
  "&.Mui-selected": {
    borderRadius: "10px",
  },
}));

const CustomPaginationItem = (props: PaginationRenderItemParams) => {
  return <StyledPaginationItem {...props} />;
};

const scrollTop = () => {
  window.focus();
  window.scrollTo(0, 0);
};

const PAGE_SIZE = 24;

const EMPTY_FILTER_OPTIONS: SkuFilterOptions = {
  brands: [],
  models: [],
  years: [],
  ccs: [],
  priceMin: null,
  priceMax: null,
};

const NewCatalog = () => {
  const { alliance } = useAlliance();

  const [isFixed, setIsFixed] = useState(true);
  const divRef = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);
  const filterContainerRef = useRef<HTMLDivElement>(null);
  const matchesXS = useMediaQuery("(min-width:600px)");
  const matchesSM = useMediaQuery("(min-width:900px)");
  const matchesMD = useMediaQuery("(min-width:1200px)", { noSsr: true });

  const checkIfShouldBeResized = () => {
    const filterContainer = filterContainerRef.current?.getBoundingClientRect();
    divRef.current!.style.width = `${filterContainer?.width}px`;

    if (matchesSM) {
      divRef.current!.style.paddingRight = "30px";
    } else {
      divRef.current!.style.paddingRight = "";
    }
  };

  const checkIfShouldBeFixed = () => {
    if (matchesSM) {
      const rect = bannerRef.current?.getBoundingClientRect();
      setIsFixed(rect ? rect.y > window.innerHeight : false);
    } else {
      setIsFixed(false);
    }
  };

  const handleScroll = () => {
    checkIfShouldBeFixed();
  };

  const handleResize = () => {
    checkIfShouldBeFixed();
    checkIfShouldBeResized();
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    checkIfShouldBeFixed();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [matchesSM]);

  useEffect(() => {
    checkIfShouldBeResized();
  }, [filterContainerRef.current?.getBoundingClientRect().width]);

  const { selectedFilters } = useSkus();

  const [modalOpen, setModalOpen] = useState(false);
  const [handleFilterChange, handleClearAll, filtersInitialized] = useSkuCatalogFilters(alliance);

  const [displayedSkus, setDisplayedSkus] = useState<TSku[]>([]);
  const [filterOptions, setFilterOptions] = useState<SkuFilterOptions>(EMPTY_FILTER_OPTIONS);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [count, setCount] = useState(0);
  const [resultsLoading, setResultsLoading] = useState(true);
  const requestIdRef = useRef(0);

  useEffect(() => {
    scrollTop();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [selectedFilters]);

  useEffect(() => {
    if (!filtersInitialized) return;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setResultsLoading(true);

    getSkus(toServerFilters(selectedFilters ?? {}, page, PAGE_SIZE))
      .then((result) => {
        if (requestIdRef.current !== requestId) return;
        setDisplayedSkus(result.skus);
        setPages(result.pages);
        setCount(result.count);
        setFilterOptions(result.filters);
      })
      .catch(() => {
        if (requestIdRef.current !== requestId) return;
        setDisplayedSkus([]);
      })
      .finally(() => {
        if (requestIdRef.current !== requestId) return;
        setResultsLoading(false);
      });
  }, [selectedFilters, page, filtersInitialized]);

  useEffect(() => {
    if (!resultsLoading) {
      scrollTop();
      setIsFixed(false);
    }
  }, [resultsLoading]);

  const scrollDirection = useScrollDirection();

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const selectedTotalFilters = selectedFilters
    ? Object.entries(selectedFilters).filter(([key, value]) => key !== "sort" && value !== undefined).length
    : 0;

  return (<>
    <MetaTags
      title="Catálogo de motos nuevas y seminuevas a crédito en CDMX"
      description="Explora nuestro catálogo de motos nuevas y seminuevas en venta en CDMX. Compra a crédito, con pagos cómodos y entrega inmediata."
    />
    <div className="p_t_xl_mobile bg_neutral_300">
      <section className={`${matchesSM ? " display_none" : "display_flex flex_col"}`}>
        <div
          className={`mobile_header_sitcky animate__animated ${
            scrollDirection === "down"
              ? "animate__slideOutUp"
              : "animate__slideInDown"
          }`}
        >
          {!alliance && <div className="p_x_lg">
            <SkuSearchInput onChange={handleFilterChange} />
          </div>}
          <div
            className="display_flex p_x_lg"
            style={{ justifyContent: "space-between", alignItems: "center" }}
          >
            <Button
              scale="small"
              className="m_r_md"
              onClick={() => setModalOpen(true)}
              icon={<Filter />}
            >
              Filtros ({selectedTotalFilters})
            </Button>
            <SortSkus onChange={handleFilterChange} />
          </div>
        </div>
      </section>

      <Container maxWidth="xl" style={{ padding: 0 }}>
        <Grid container spacing={0} style={{ padding: "0 3vw" }}>

          <Grid
            item
            xs={12}
            sm={12}
            md={4}
            lg={3}
            style={{ display: !matchesSM ? "none" : "" }}
            ref={filterContainerRef}
          >
            <div
              ref={divRef}
              style={{
                position: isFixed ? "fixed" : "static",
                display: isFixed ? "block" : "",
              }}
            >
              <SkuSideBar
                open
                onChange={handleFilterChange}
                filterOptions={filterOptions}
                loading={resultsLoading}
              />
            </div>
          </Grid>

          <Grid
            item
            xs={12}
            sm={12}
            md={8}
            lg={9}
            style={{
              marginTop: !matchesSM ? "105px" : "0",
            }}
          >
            <div style={{
              display: !matchesSM ? "none" : "",
              paddingLeft: 12,
              paddingRight: 12,
              marginTop: 25,
              marginBottom: 25,
            }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "space-between",
                  gap: 8,
                  marginBottom: 5,
                }}
              >
                <div className="display_none_mobile">
                  {count > 0 && !resultsLoading && (
                    <div className="display_flex flex_align_center">
                      <Typography scale="medium" className="display_flex" weight="400">
                        <b className="m_r_xs">{count} </b> moto
                        {count !== 1 ? "s" : ""}
                      </Typography>
                    </div>
                  )}
                </div>
                <SortSkus onChange={handleFilterChange} />
              </div>

              <SkuFilterTagList onChange={handleFilterChange} onClearAll={handleClearAll} />
            </div>

            {resultsLoading ? (
              <div className="m_t_md m_b_lg">
                <SkeletonCard
                  showItems={!matchesXS ? 4 : 8}
                  columnsPerCard={!matchesXS ? 12 : !matchesMD ? 4 : 3}
                />
              </div>
            ) : (
              <OzonCardVehiclesPagination
                skus={displayedSkus}
              />
            )}

            {pages > 1 && <div
              className="m_b_xl"
              style={{ display: "flex", padding: 20, justifyContent: "center" }}
            >
              <Pagination
                count={pages}
                page={page}
                onChange={handlePageChange}
                renderItem={(props) => <CustomPaginationItem {...props} />}
                sx={{
                  "& .MuiPagination-ul li": {
                    background: "none",
                    border: "none",
                  },
                }}
              />
            </div>}
            <br />
          </Grid>

        </Grid>
      </Container>

      <div ref={bannerRef}>
        {!alliance && <CatalogBanner />}
      </div>
    </div>
    <SkuFiltersModal
      count={count}
      open={modalOpen}
      setOpen={setModalOpen}
      onChange={handleFilterChange}
      onClearAll={handleClearAll}
      filterOptions={filterOptions}
      loading={resultsLoading}
    />
  </>
  );
};

export default NewCatalog;
